import { ArrowRightIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useMemo } from 'react';

import { ActionName } from '@invenco/common-domain/enums';

import {
  AlertDialogWithAction,
  AlertDialogWithActionCancel,
  AlertDialogWithActionContent,
  AlertDialogWithActionDescription,
  AlertDialogWithActionFooter,
  AlertDialogWithActionHeader,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionTitle,
  AlertDialogWithActionTrigger,
} from '@/components/alert-dialog-with-action';
import { Button } from '@/components/core/button';
import { cn } from '@/shared/helpers';
import { Result } from '@/shared/helpers/Result';

import { EditorActionBranch } from '../editor-model';
import { flushClassName, SingleSelectCombobox, type Option } from '../field-input';
import { getActionDefinition } from '../field-registry';
import { WorkflowEditorState } from '../use-workflow-editor-state';
import { actionParams, enabledActionTypes, setActionParam, setActionType } from './action-draft';
import { ActionParamField } from './action-param-field';

/**
 * Side panel for editing a selected action: a searchable type select plus the
 * type's registry-driven param fields, staged through `updateAction`. Delete
 * is confirmed — staged removal has no undo.
 */
export function ActionPanel({
  editor,
  ruleIndex,
  branch,
  actionIndex,
}: {
  editor: WorkflowEditorState;
  ruleIndex: number;
  branch: EditorActionBranch;
  actionIndex: number;
}) {
  const { scopeType } = editor.context;
  const action = editor.rules[ruleIndex][branch][actionIndex];
  const definition = action.type ? getActionDefinition(action.type) : undefined;
  const params = actionParams(action);

  const typeOptions = useMemo<Option[]>(
    () =>
      enabledActionTypes(scopeType).map((type) => ({
        value: type,
        label: getActionDefinition(type).title,
      })),
    [scopeType],
  );

  const onTypeChange = (type: ActionName) => {
    // A same-type reselect must not restart the grace period.
    if (type !== action.type) {
      editor.updateAction(ruleIndex, branch, actionIndex, (current) =>
        setActionType(current, type),
      );
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b p-3">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ArrowRightIcon className="size-3.5" />
        </span>
        <span className="min-w-0 flex-1 truncate text-base font-medium">Action</span>
        <ActionDeleteButton onDelete={() => editor.removeAction(ruleIndex, branch, actionIndex)} />
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Close"
          onClick={() => editor.select(null)}
        >
          <XIcon className="size-4" />
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="overflow-hidden rounded-lg border border-input bg-card [&>*+*]:border-t [&>*+*]:border-input">
          <SingleSelectCombobox
            aria-label="Action type"
            options={typeOptions}
            value={action.type}
            placeholder="Select an action"
            emptyText="No matching actions"
            // No clearing back to typeless — a picked type can only be switched.
            showClear={false}
            className={cn('w-full', flushClassName)}
            onChange={(next) => {
              if (typeof next === 'string') {
                onTypeChange(next as ActionName);
              }
            }}
          />
          {definition && definition.params.length > 0 && (
            <div className="grid @md/side:grid-cols-2">
              {definition.params.map((param) => (
                <div
                  key={param.key}
                  // Dividers sit on the cell, not the input, whose focus border
                  // would repaint shared edges.
                  className="min-w-0 border-input not-first:border-t @md/side:odd:last:col-span-2 @md/side:even:border-l @md/side:nth-2:border-t-0"
                >
                  <ActionParamField
                    param={param}
                    value={params[param.key]}
                    fresh={!!action.fresh}
                    onChange={(value) =>
                      editor.updateAction(ruleIndex, branch, actionIndex, (current) =>
                        setActionParam(current, param.key, value),
                      )
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** The delete action, gated behind a confirm dialog (staged removal has no undo). */
function ActionDeleteButton({ onDelete }: { onDelete: () => void }) {
  const confirmDelete = async (): Promise<Result> => {
    onDelete();
    return Result.ok();
  };
  return (
    <AlertDialogWithAction onAction={confirmDelete}>
      <AlertDialogWithActionTrigger asChild>
        <Button type="button" variant="ghost" size="icon" aria-label="Delete action">
          <Trash2Icon className="size-4" />
        </Button>
      </AlertDialogWithActionTrigger>
      <AlertDialogWithActionContent>
        <AlertDialogWithActionHeader>
          <AlertDialogWithActionTitle>Delete this action?</AlertDialogWithActionTitle>
          <AlertDialogWithActionDescription>
            It will be removed from this branch when you save.
          </AlertDialogWithActionDescription>
        </AlertDialogWithActionHeader>
        <AlertDialogWithActionFooter>
          <AlertDialogWithActionCancel>Cancel</AlertDialogWithActionCancel>
          <AlertDialogWithActionSubmit>Delete</AlertDialogWithActionSubmit>
        </AlertDialogWithActionFooter>
      </AlertDialogWithActionContent>
    </AlertDialogWithAction>
  );
}
